import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, Users, Search, Filter, Link, Unlink, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getContract, getContractCustomers, addContractCustomer, removeContractCustomer, getCustomers } from '../../config/api';

const ContractCustomers = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [showMultiAddForm, setShowMultiAddForm] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    customerId: '',
    priceNotifyMode: 'CHANGES'
  });

  // Multi-add form state
  const [multiAddFormData, setMultiAddFormData] = useState({
    selectedCustomers: [],
    priceNotifyMode: 'CHANGES'
  });

  // Fetch contract details
  const { data: contract, isLoading: contractLoading } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => getContract(id),
    enabled: !!id
  });

  // Fetch contract customers
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['contract-customers', id],
    queryFn: () => getContractCustomers(id),
    enabled: !!id
  });

  // Fetch all customers for selection
  const { data: allCustomersData } = useQuery({
    queryKey: ['customers'],
    queryFn: getCustomers,
    enabled: showCustomerSelector
  });

  const contractCustomers = customersData?.customers || [];
  const allCustomers = allCustomersData?.customers || [];

  // Mutations
  const addMutation = useMutation({
    mutationFn: (data) => {
      console.log('Adding contract customer with data:', data);
      return addContractCustomer(id, data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contract-customers', id] });
      toast.success('Customer added to contract successfully');
      setShowAddForm(false);
      resetForm();
    },
    onError: (error) => {
      console.error('Add contract customer error:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.error || 'Failed to add customer to contract';
      const errorCode = error.response?.data?.code;
      
      if (errorCode === 'DUPLICATE_RELATIONSHIP') {
        toast.error('This customer is already associated with this contract');
      } else if (error.response?.status === 404) {
        toast.error('Contract or customer not found');
      } else if (error.response?.status === 400) {
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage);
      }
    }
  });



  const removeMutation = useMutation({
    mutationFn: (customerId) => removeContractCustomer(id, customerId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contract-customers', id] });
      toast.success('Customer removed from contract successfully');
    },
    onError: (error) => {
      console.error('Remove contract customer error:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.error || 'Failed to remove customer from contract';
      const errorCode = error.response?.data?.code;
      
      if (errorCode === 'RELATIONSHIP_NOT_FOUND') {
        toast.error('Customer is not associated with this contract');
      } else if (error.response?.status === 404) {
        toast.error('Contract or customer not found');
      } else {
        toast.error(errorMessage);
      }
    }
  });

  const multiAddMutation = useMutation({
    mutationFn: async (data) => {
      console.log('Multi-adding contract customers with data:', data);
      // Add multiple customers one by one
      const results = [];
      for (const customerId of data.selectedCustomers) {
        try {
          console.log(`Adding customer ${customerId} with settings:`, {
            customerId,
            priceNotifyMode: data.priceNotifyMode
          });
          const result = await addContractCustomer(id, {
            customerId,
            priceNotifyMode: data.priceNotifyMode
          });
          results.push({ customerId, success: true, result });
        } catch (error) {
          console.error(`Failed to add customer ${customerId}:`, error);
          results.push({ customerId, success: false, error: error.message });
        }
      }
      return results;
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries({ queryKey: ['contract-customers', id] });
      const successCount = results.filter(r => r.success).length;
      const errorCount = results.filter(r => !r.success).length;
      
      if (successCount > 0) {
        toast.success(`Successfully added ${successCount} customer(s) to contract`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} customer(s) failed to add`);
      }
      
      setShowMultiAddForm(false);
      resetMultiAddForm();
      resetCustomerSearch();
    },
    onError: (error) => {
      toast.error('Failed to add multiple customers');
    }
  });

  const resetForm = () => {
    setFormData({
      customerId: '',
      priceNotifyMode: 'CHANGES'
    });
  };

  const resetMultiAddForm = () => {
    setMultiAddFormData({
      selectedCustomers: [],
      priceNotifyMode: 'CHANGES'
    });
  };

  const resetCustomerSearch = () => {
    setCustomerSearchTerm('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    addMutation.mutate(formData);
  };



  const handleRemove = (customerId) => {
    if (window.confirm('Are you sure you want to remove this customer from the contract?')) {
      removeMutation.mutate(customerId);
    }
  };

  const handleCustomerSelect = (customer) => {
    setFormData({ ...formData, customerId: customer.id });
    setShowCustomerSelector(false);
  };

  const handleMultiCustomerSelect = (customer) => {
    setMultiAddFormData(prev => ({
      ...prev,
      selectedCustomers: prev.selectedCustomers.includes(customer.id) 
        ? prev.selectedCustomers.filter(id => id !== customer.id)
        : [...prev.selectedCustomers, customer.id]
    }));
  };

  const handleMultiAddSubmit = (e) => {
    e.preventDefault();
    
    if (multiAddFormData.selectedCustomers.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }
    
    multiAddMutation.mutate(multiAddFormData);
  };

  const filteredCustomers = contractCustomers.filter(customer => {
    return !searchTerm || 
      customer.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.customer?.ediEntityId?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const availableCustomers = allCustomers.filter(customer => 
    !contractCustomers.some(cc => cc.customerId === customer.id)
  );

  // Filter available customers based on search term
  const filteredAvailableCustomers = availableCustomers.filter(customer => {
    if (!customerSearchTerm) return true;
    
    const searchLower = customerSearchTerm.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(searchLower) ||
      customer.ediEntityId?.toLowerCase().includes(searchLower) ||
      customer.type?.toLowerCase().includes(searchLower)
    );
  });

  if (contractLoading || customersLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contract not found</h2>
          <button
            onClick={() => navigate('/app/contracts')}
            className="text-primary-600 hover:text-primary-700"
            title="Back to Contracts"
          >
            Back to Contracts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/app/contracts')}
            className="text-gray-400 hover:text-gray-600"
            title="Back to Contracts"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Contract Customers</h1>
            <p className="text-sm text-gray-500">
              {contract.number} - {contract.description}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowMultiAddForm(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
            title="Add Multiple Customers"
          >
            <Users className="h-4 w-4 mr-2" />
            Add Multiple Customers
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            title="Add Customer"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Customer
          </button>
        </div>
      </div>

      {/* Contract Summary */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Contract Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Type</p>
            <p className="font-medium">{contract.type}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <p className="font-medium">{contract.status}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Effective Period</p>
            <p className="font-medium">
              {new Date(contract.effectiveFrom).toLocaleDateString()} - {new Date(contract.effectiveTo).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Add Customer to Contract
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Select customer..."
                    value={formData.customerId ? 
                      allCustomers.find(c => c.id === formData.customerId)?.name || 'Unknown Customer' : 
                      ''
                    }
                    onClick={() => setShowCustomerSelector(true)}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:border-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCustomerSelector(true)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Search customers"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price Notify Mode
                </label>
                <select
                  value={formData.priceNotifyMode}
                  onChange={(e) => setFormData({ ...formData, priceNotifyMode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="CHANGES">Changes Only</option>
                  <option value="FULL">Full Notification</option>
                </select>
              </div>
            </div>
            

            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                title="Cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={addMutation.isPending || !formData.customerId}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                title="Add Customer"
              >
                Add Customer
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Multi-Add Customers Form */}
      {showMultiAddForm && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Add Multiple Customers to Contract</h3>
          
          <form onSubmit={handleMultiAddSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price Notify Mode
                </label>
                <select
                  value={multiAddFormData.priceNotifyMode}
                  onChange={(e) => setMultiAddFormData({ ...multiAddFormData, priceNotifyMode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="CHANGES">Changes Only</option>
                  <option value="FULL">Full Notification</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Selected Customers ({multiAddFormData.selectedCustomers.length})
                </label>
                <button
                  type="button"
                  onClick={() => setShowCustomerSelector(true)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-left text-gray-700 hover:border-gray-400 bg-white"
                  title="Select customers"
                >
                  {multiAddFormData.selectedCustomers.length === 0 
                    ? 'Click to select customers...' 
                    : `${multiAddFormData.selectedCustomers.length} customer(s) selected`
                  }
                </button>
              </div>
            </div>
            


            {multiAddFormData.selectedCustomers.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Customers:</h4>
                <div className="space-y-2">
                  {multiAddFormData.selectedCustomers.map(customerId => {
                    const customer = allCustomers.find(c => c.id === customerId);
                    return customer ? (
                      <div key={customerId} className="flex items-center justify-between bg-white p-2 rounded border">
                        <div>
                          <div className="font-medium text-gray-900">{customer.name}</div>
                          <div className="text-sm text-gray-500">
                            EDI: {customer.ediEntityId || 'N/A'} | Type: {customer.type || 'N/A'}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleMultiCustomerSelect(customer)}
                          className="text-red-600 hover:text-red-800"
                          title="Remove"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowMultiAddForm(false);
                  resetMultiAddForm();
                  resetCustomerSearch();
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                title="Cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={multiAddMutation.isPending || multiAddFormData.selectedCustomers.length === 0}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                title="Add selected customers"
              >
                {multiAddMutation.isPending ? 'Adding...' : `Add ${multiAddFormData.selectedCustomers.length} Customer(s)`}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Customer Selector Modal */}
      {showCustomerSelector && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {showMultiAddForm ? 'Select Multiple Customers' : 'Select Customer'}
                </h3>
                <button
                  onClick={() => {
                    setShowCustomerSelector(false);
                    resetCustomerSearch();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  title="Close"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              {showMultiAddForm && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800">
                    Click on customers to select/deselect them. Selected customers will be highlighted.
                  </p>
                </div>
              )}

              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search customers by name, EDI, or type..."
                    value={customerSearchTerm}
                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                  {customerSearchTerm && (
                    <button
                      onClick={resetCustomerSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {filteredAvailableCustomers.length === 0 ? (
                  <div className="text-center py-8">
                    {customerSearchTerm ? (
                      <div>
                        <p className="text-gray-500">No customers found matching "{customerSearchTerm}"</p>
                        <button
                          onClick={resetCustomerSearch}
                          className="mt-2 text-primary-600 hover:text-primary-700 text-sm"
                          title="Clear search"
                        >
                          Clear search
                        </button>
                      </div>
                    ) : (
                      <p className="text-gray-500">No available customers to add</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredAvailableCustomers.map((customer) => {
                      const isSelected = showMultiAddForm && multiAddFormData.selectedCustomers.includes(customer.id);
                      return (
                        <button
                          key={customer.id}
                          onClick={() => showMultiAddForm ? handleMultiCustomerSelect(customer) : handleCustomerSelect(customer)}
                          title="Select customer"
                          className={`w-full text-left p-3 border rounded-lg transition-colors ${
                            isSelected 
                              ? 'border-primary-500 bg-primary-50 text-primary-900' 
                              : 'border-gray-200 hover:bg-gray-50 hover:border-primary-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-gray-900">{customer.name}</div>
                              <div className="text-sm text-gray-500">
                                EDI: {customer.ediEntityId || 'N/A'} | Type: {customer.type || 'N/A'}
                              </div>
                            </div>
                            {showMultiAddForm && isSelected && (
                              <div className="text-primary-600">
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {showMultiAddForm && (
                <div className="mt-4 flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    {multiAddFormData.selectedCustomers.length} customer(s) selected
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomerSelector(false);
                      resetCustomerSearch();
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                    title="Done"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Contract Customers ({filteredCustomers.length})</h3>
        </div>
        
        {filteredCustomers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No customers found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm 
                ? 'Try adjusting your search.'
                : 'Get started by adding your first customer to this contract.'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    EDI Entity ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price Notify Mode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCustomers.map((contractCustomer) => (
                  <tr key={contractCustomer.customerId}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {contractCustomer.customer?.name || 'Unknown Customer'}
                        </div>

                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {contractCustomer.customer?.ediEntityId || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {contractCustomer.customer?.type || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {contractCustomer.priceNotifyMode || 'CHANGES'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleRemove(contractCustomer.customerId)}
                          className="text-red-600 hover:text-red-900"
                          title="Remove"
                        >
                          <Unlink className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractCustomers;
